import * as React from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, MenuItem, Pagination, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Dayjs } from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getVirtualCatalogSports, getVirtualProviderLeague, getVirtualProviderMatches } from '../Api';
import VirtualCatalogAddMenu from '../components/virtual-sports/VirtualCatalogAddMenu';
import VirtualMatchCreationDialog from '../components/virtual-sports/VirtualMatchCreationDialog';
import VirtualMatchEditDialog from '../components/virtual-sports/VirtualMatchEditDialog';
import VirtualMatchGenerationDialog from '../components/virtual-sports/VirtualMatchGenerationDialog';
import VirtualMatchesTable from '../components/virtual-sports/VirtualMatchesTable';
import VirtualActivityLogBell from '../components/virtual-sports/VirtualActivityLogBell';
import { VirtualCatalogSportDto } from '../models/dtos/VirtualSportsCatalogDto';
import { VirtualMatchState, VirtualProviderLeagueDto, VirtualProviderMatchSummaryDto, VirtualProviderTeamDto } from '../models/dtos/VirtualProviderCatalogDto';
import VirtualLeagueSearchSelect from '../components/virtual-sports/VirtualLeagueSearchSelect';
import VirtualTeamSearchSelect from '../components/virtual-sports/VirtualTeamSearchSelect';
import { useOpenVirtualMatch } from '../components/virtual-sports/useOpenVirtualMatch';

const PAGE_SIZE = 25;

const MATCH_STATES: VirtualMatchState[] = ['Future', 'Live', 'Finished', 'Processing', 'Cancelled'];

export default function VirtualSportsMatchesPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // The provider identifies leagues by sync id, which is what this page filters on. An id in the
    // query string (from "View matches" on a league) seeds the dropdown until it is changed here.
    const leagueIdFromUrl = searchParams.get('leagueId')?.trim() || null;
    const [page, setPage] = React.useState(0);
    const [league, setLeague] = React.useState<VirtualProviderLeagueDto | null>(null);
    const [team, setTeam] = React.useState<VirtualProviderTeamDto | null>(null);
    const [state, setState] = React.useState<VirtualMatchState | ''>('');
    const [startFrom, setStartFrom] = React.useState<Dayjs | null>(null);
    const [startTo, setStartTo] = React.useState<Dayjs | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [matches, setMatches] = React.useState<VirtualProviderMatchSummaryDto[]>([]);
    const [totalItems, setTotalItems] = React.useState(0);
    const [sports, setSports] = React.useState<VirtualCatalogSportDto[]>([]);
    const [createOpen, setCreateOpen] = React.useState(false);
    const [generateOpen, setGenerateOpen] = React.useState(false);
    const [editingMatch, setEditingMatch] = React.useState<VirtualProviderMatchSummaryDto | null>(null);
    const { openMatch, openingSyncId, error: openError, clearError } = useOpenVirtualMatch();
    const [reloadKey, setReloadKey] = React.useState(0);
    const [error, setError] = React.useState('');
    const [notice, setNotice] = React.useState('');

    React.useEffect(() => {
        getVirtualCatalogSports().then(setSports).catch(reason =>
            setError(reason instanceof Error ? reason.message : 'Could not load virtual sports'));
    }, []);

    // Resolve a league arrived at from another page into the dropdown's own selection.
    React.useEffect(() => {
        if (!leagueIdFromUrl || league?.id === leagueIdFromUrl) return;
        getVirtualProviderLeague(leagueIdFromUrl).then(setLeague).catch(() => setLeague(null));
    }, [leagueIdFromUrl, league?.id]);

    const invalidRange = startFrom != null && startTo != null && startTo.isBefore(startFrom);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        if (invalidRange) {
            setLoading(false);
            return;
        }
        const timeout = setTimeout(() => {
            getVirtualProviderMatches(page * PAGE_SIZE, PAGE_SIZE, {
                leagueId: league?.id ?? leagueIdFromUrl,
                teamId: team?.id ?? null,
                state: state || null,
                startFrom: startFrom?.toISOString() ?? null,
                startTo: startTo?.toISOString() ?? null,
            })
                .then(result => {
                    if (cancelled) return;
                    setMatches(result.items);
                    setTotalItems(result.totalItems);
                    setLoading(false);
                })
                .catch(reason => {
                    if (cancelled) return;
                    setError(reason instanceof Error ? reason.message : 'Could not load virtual matches');
                    setLoading(false);
                });
        }, 0);
        return () => {
            cancelled = true;
            clearTimeout(timeout);
        };
    }, [page, league, leagueIdFromUrl, team, state, startFrom, startTo, invalidRange, reloadKey]);

    const clearLeagueFilter = () => {
        setSearchParams({});
        setPage(0);
    };

    const hasFilters = league != null || team != null || state !== '' || startFrom != null || startTo != null;

    const clearAllFilters = () => {
        setLeague(null);
        setTeam(null);
        setState('');
        setStartFrom(null);
        setStartTo(null);
        clearLeagueFilter();
    };

    const matchScheduled = (homeTeamName: string, awayTeamName: string) => {
        setNotice(`${homeTeamName} vs ${awayTeamName} was scheduled. It will appear here after catalog synchronization.`);
        setPage(0);
        setReloadKey(value => value + 1);
    };

    return (
        <Stack spacing={3} sx={{ mx: { xs: 2, md: 5 }, my: 5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h3">Virtual matches</Typography>
                    <Typography color="text.secondary">
                        {totalItems.toLocaleString()} {totalItems === 1 ? 'match' : 'matches'} in the virtual sports catalog, newest kick-off first.
                    </Typography>
                </Box>
                <VirtualCatalogAddMenu
                    entityLabel="match"
                    addLabel="Create match"
                    generateLabel="Generate match"
                    onAdd={() => setCreateOpen(true)}
                    onGenerateImmediately={() => setGenerateOpen(true)}
                />
                <VirtualActivityLogBell />
                <Button variant="outlined" onClick={() => navigate('/virtual-sports')}>Back to overview</Button>
            </Stack>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2} columns={{ xs: 1, sm: 2, lg: 5 }} alignItems="center">
                    <Grid item xs={1} sm={1} lg={1}>
                        <VirtualLeagueSearchSelect
                            value={league}
                            onChange={value => { setLeague(value); setPage(0); if (!value) clearLeagueFilter(); }}
                            label="League (optional)"
                        />
                    </Grid>
                    <Grid item xs={1} sm={1} lg={1}>
                        <VirtualTeamSearchSelect
                            value={team}
                            onChange={value => { setTeam(value); setPage(0); }}
                            label="Team (optional)"
                        />
                    </Grid>
                    <Grid item xs={1} sm={1} lg={1}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Status (optional)"
                            value={state}
                            onChange={event => { setState(event.target.value as VirtualMatchState | ''); setPage(0); }}
                        >
                            <MenuItem value="">Any status</MenuItem>
                            {MATCH_STATES.map(option => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={1} sm={1} lg={1}>
                        <DateTimePicker
                            label="Kick-off from (optional)"
                            value={startFrom}
                            onChange={value => { setStartFrom(value); setPage(0); }}
                            slotProps={{
                                textField: { fullWidth: true, size: 'small' },
                                field: { clearable: true },
                            }}
                        />
                    </Grid>
                    <Grid item xs={1} sm={1} lg={1}>
                        <DateTimePicker
                            label="Kick-off to (optional)"
                            value={startTo}
                            minDateTime={startFrom ?? undefined}
                            onChange={value => { setStartTo(value); setPage(0); }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    error: invalidRange,
                                    helperText: invalidRange ? 'Must be after the "from" time' : undefined,
                                },
                                field: { clearable: true },
                            }}
                        />
                    </Grid>
                </Grid>
            </LocalizationProvider>

            {hasFilters ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    {league ? <Chip label={`League: ${league.name}`} onDelete={() => { setLeague(null); clearLeagueFilter(); }} variant="outlined" /> : null}
                    {team ? <Chip label={`Team: ${team.name}`} onDelete={() => setTeam(null)} variant="outlined" /> : null}
                    {state ? <Chip label={`Status: ${state}`} onDelete={() => setState('')} variant="outlined" /> : null}
                    {startFrom ? <Chip label={`From: ${startFrom.format('YYYY-MM-DD HH:mm')}`} onDelete={() => setStartFrom(null)} variant="outlined" /> : null}
                    {startTo ? <Chip label={`To: ${startTo.format('YYYY-MM-DD HH:mm')}`} onDelete={() => setStartTo(null)} variant="outlined" /> : null}
                    <Button size="small" onClick={clearAllFilters}>Clear all</Button>
                </Stack>
            ) : null}

            <Card variant="outlined">
                <CardContent>
                    {openError ? <Alert severity="warning" sx={{ mb: 2 }} onClose={clearError}>{openError}</Alert> : null}
                    {loading ? <Box textAlign="center"><CircularProgress /></Box> : (
                        <VirtualMatchesTable
                            matches={matches}
                            onOpenMatch={openMatch}
                            openingSyncId={openingSyncId}
                            onEditMatch={setEditingMatch}
                        />
                    )}
                </CardContent>
            </Card>

            {totalItems > PAGE_SIZE ? (
                <Pagination
                    count={Math.ceil(totalItems / PAGE_SIZE)}
                    page={page + 1}
                    onChange={(_, value) => setPage(value - 1)}
                    sx={{ alignSelf: 'center' }}
                />
            ) : null}

            <VirtualMatchCreationDialog
                open={createOpen}
                sports={sports}
                onClose={() => setCreateOpen(false)}
                onCreated={result => matchScheduled(result.match.homeTeam.name, result.match.awayTeam.name)}
            />
            <VirtualMatchGenerationDialog
                open={generateOpen}
                sports={sports}
                onClose={() => setGenerateOpen(false)}
                onGenerated={result => matchScheduled(result.match.homeTeam.name, result.match.awayTeam.name)}
            />
            <VirtualMatchEditDialog match={editingMatch} onClose={() => setEditingMatch(null)} />
            <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice('')} message={notice} />
        </Stack>
    );
}
